// features/findings/korea-lockin.js
// ════════════════════════════════════════════════════════════════════
// LAB FINDING — Korea's Lock-In: A Counterfactual Walk (v1)
// Form 5 (Counterfactual walk). Third published Lab finding.
// Content sourced from docs/LAB_FINDING_KOREA_LOCKIN_V1.md
// ════════════════════════════════════════════════════════════════════
// Reflexivity HIGH · Population-decline domain. Demonstrates Form 5's
// branch-points → counterfactual paths → levers → lockings structure.
// Pre-registered 2026-05-07.
// ════════════════════════════════════════════════════════════════════

import {
  FindingBack,
  FindingHeader,
  FindingSection,
  FindingSubhead,
  FindingPill,
  FindingPillRow,
  FindingCite,
} from './components-shared';
import {
  DivergenceTimeline,
  CausalChainDiagram,
  TornadoPlot,
  PredictionsBlock,
  MagnitudeBars,
} from './charts';

export const FINDING_META = {
  slug: 'korea-lockin',
  domain: 'popdec',
  year: 2026,
  version: 'v1',
  lifecycle: 'pre-registered',
  prevVersion: null,
  revisedBy: null,
  whatChanged: null,
  retractionNote: null,
  citationHandle: 'lab:finding/popdec/2026/korea-lockin/v1',
  title: "Korea's Lock-In: A Counterfactual Walk Through Six Branch Points",
  shortTitle: "Korea's Lock-In",
  pageTitle: "Korea's Lock-In — Lab Finding · Shift",
  description: "Six branch points in Korea's fertility decline 1997-2024. Three were levers. Three were lockings. Telling them apart is what makes the path-dependence real.",
  form: 5,
  formName: 'Counterfactual walk',
  reflexivity: 'HIGH',
  epistemicStatus: 'mechanism',
  confidence: 'medium',
  sampleSize: 1, // 1 trajectory; 6 branch points within
  preregisteredAt: '2026-05-07',
  visibility: 'public-peek',
};

// ─── Chart data ─────────────────────────────────────────────────────

const KOREA_TFR_SERIES = [
  {
    id: 'korea',
    label: 'Korea',
    color: 'oxblood',
    points: [
      { x: 1990, y: 1.57 },
      { x: 1995, y: 1.63 },
      { x: 2000, y: 1.45 },
      { x: 2003, y: 1.16 },
      { x: 2005, y: 1.08 },
      { x: 2010, y: 1.23 },
      { x: 2015, y: 1.24 },
      { x: 2018, y: 0.98 },
      { x: 2021, y: 0.81 },
      { x: 2023, y: 0.72 },
      { x: 2024, y: 0.75 },
    ],
  },
];

const BRANCH_MARKERS = [
  { x: 1997, label: 'B1 — IMF crisis (lever)', tone: 'oxblood' },
  { x: 2003, label: 'B2 — housing bubble (lever)', tone: 'oxblood' },
  { x: 2005, label: 'B3 — first plan (partial)', tone: 'cognac' },
  { x: 2015, label: 'B4 — gender backlash (locking)', tone: 'plum' },
  { x: 2020, label: 'B5 — COVID + housing (locking)', tone: 'plum' },
  { x: 2024, label: 'B6 — recognition window', tone: 'sage' },
];

const LEVER_RANKING = [
  {
    id: 'b1', label: 'Branch 1 — 1997 IMF restructuring',
    value: 0.20, valueLabel: '~0.20 TFR · LEVER',
    tone: 'oxblood',
  },
  {
    id: 'b3', label: 'Branch 3 — 2005 plan intensity (tempo only)',
    value: 0.15, valueLabel: '~0.15 TFR tempo · LEVER',
    tone: 'cognac',
  },
  {
    id: 'b2', label: 'Branch 2 — 2002-05 housing bubble',
    value: 0.075, valueLabel: '~0.075 TFR · LEVER',
    tone: 'oxblood',
  },
  {
    id: 'b4', label: 'Branch 4 — 2015 gender backlash',
    value: 0.035, valueLabel: '~0.03 TFR · LOCKING',
    tone: 'locking',
  },
  {
    id: 'b5', label: 'Branch 5 — 2020 COVID + housing',
    value: 0.02, valueLabel: '~0.02 TFR · LOCKING',
    tone: 'locking',
  },
  {
    id: 'b6', label: 'Branch 6 — 2024 recognition window',
    value: 0.01, valueLabel: 'fertility ≈ 0 · institutional adaptation',
    tone: 'sage',
  },
];

const LOCKIN_CHAIN = [
  { label: 'Structural lever (employment / housing / family-formation infrastructure)', confidence: 0.85 },
  { label: 'Infrastructure decision cements at policy moment (1997 / 2002-05)', confidence: 0.80 },
  { label: 'Cohorts internalize structural conditions (1998-2010 cohort dynamics)', confidence: 0.75 },
  { label: 'Subsequent policy can produce tempo effects but not cohort change', confidence: 0.80 },
  { label: 'Lock-in: each downstream branch becomes a locking, not a lever', confidence: 0.85 },
];

const LOCKIN_FALSIFIER = [
  {
    id: 'branch-classification',
    label: 'Lever-vs-locking classification',
    low: -0.20,
    high: 0.10,
    lowLabel: 'B4-5 actually levers',
    highLabel: 'B1-3 actually lockings',
  },
  {
    id: 'counterfactual-magnitude',
    label: 'Cumulative counterfactual magnitude',
    low: -0.30,
    high: 0.15,
    lowLabel: 'overstated; lockings dominate',
    highLabel: 'understated; levers larger',
  },
  {
    id: 'cross-country-replication',
    label: 'Cross-country replication',
    low: -0.25,
    high: 0.20,
    lowLabel: 'pattern Korea-specific',
    highLabel: 'generalizes broadly',
  },
  {
    id: 'cohort-completion',
    label: 'Cohort completion data (~2030)',
    low: -0.15,
    high: 0.30,
    lowLabel: 'cohort trajectory deeper',
    highLabel: 'cohort recovery from below',
  },
];

export const LOCKIN_PREDICTIONS = [
  {
    label: 'Korea TFR 2027',
    band: [0.70, 0.90],
    central: 0.78,
    falsifierBand: [0.65, 1.00],
    resolutionDate: '2028 Statistics Korea release',
    status: 'open',
    note: 'Tests whether Branch 6 produces sustained tempo bounce or whether the underlying cohort trajectory continues. Aligned with the Korea-Hungary pair finding\'s Korea prediction.',
  },
  {
    label: 'Korea cohort fertility for women born 1990-1995 (when complete, ~2030)',
    band: [0.85, 1.10],
    central: 0.97,
    falsifierBand: [0.80, 1.20],
    resolutionDate: '2030+ KOSIS cohort completion data',
    status: 'open',
    note: 'Tests the locking-not-lever claim for Branches 4-5 directly. If completed cohort fertility is meaningfully above 1.10, the locking framing weakens for those branches.',
  },
  {
    label: 'Anti-prediction: no currently-active intervention package across declining-fertility OECD countries produces sustained cohort recovery (post-tempo) over 2025-2035.',
    resolutionDate: '2030+ multi-country cohort data',
    status: 'open',
    note: 'Aligns with the Korea-Hungary pair finding. Cross-references the broader population-decline corpus.',
  },
];

// ─── Component ──────────────────────────────────────────────────────

export default function KoreaLockinFinding() {
  return (
    <article className="finding-article">
      <FindingBack href="/findings" label="← Findings index" />

      <FindingHeader
        eyebrow="Population Decline · Korea lock-in · 2026-05-07"
        title="Korea's Lock-In"
        sub="A counterfactual walk through six branch points in the fertility decline."
        subItalic="Three were levers. Three were lockings. Telling them apart is what makes the path-dependence real — and what determines whether other countries are still in time."
        meta={FINDING_META}
      />

      {/* ─── Lede ─── */}
      <section className="finding-lede">
        <p>
          Korea&rsquo;s TFR fell from 1.7 in 1995 to 0.72 in 2023 — a decline so steep it&rsquo;s structurally unprecedented for a peacetime modern economy. From the inside it looks like one continuous descent. From a counterfactual perspective, it has six clear branch points spanning 1997-2024 where different decisions or different conditions might have produced a different trajectory. Tracing them carefully reveals that the <em>levers</em> — moments where intervention would have mattered — closed earlier than most retrospective accounts assume. By the time the 2005 master plan was drafted, three of the six branch points had already closed. By 2015, all but one had. <strong>The path-dependent nature of fertility decline is the lock-in story; the branch points are what make path-dependence real.</strong>
        </p>
      </section>

      {/* ─── TL;DR pill card ─── */}
      <section className="finding-tldr">
        <div className="finding-tldr-eyebrow">TL;DR</div>
        <p className="finding-tldr-verdict">
          Korea&rsquo;s fertility decline is a documented case of <strong>path-dependent lock-in</strong>. Six branch points existed across 1997-2024; three were <strong>levers</strong> (intervention would have changed the trajectory); three were <strong>lockings</strong> (decline was already structurally embedded).
        </p>
        <FindingPillRow>
          <FindingPill tone="mechanism">Mechanism documented</FindingPill>
          <FindingPill tone="warn">Confidence: medium</FindingPill>
          <FindingPill tone="neutral">N=1 trajectory · 6 branch points</FindingPill>
          <FindingPill tone="neutral">Pre-registered 2026-05-07</FindingPill>
          <FindingPill tone="failure">Reflexivity: HIGH</FindingPill>
        </FindingPillRow>
        <div className="finding-tldr-corpus">
          <strong>Anchor cases:</strong> Italy + Spain (similar trajectory shape, less severe) · Hungary (Korea-Hungary pair finding) · Israel (sustained recovery via different mechanism). Cross-references <FindingCite handle="lab:finding/popdec/2026/korea-hungary-divergence/v1" />.
        </div>
      </section>

      {/* ─── § The observed outcome ─── */}
      <FindingSection eyebrow="§ The observed outcome" title="Korea's TFR trajectory 1990-2024.">
        <p className="finding-prose">
          A 60% decline over 30 years, ending at the lowest sustained TFR ever observed in a peacetime modern economy. The 2024 uptick (0.72 → 0.75) may be tempo recovery from COVID-delayed marriages; the underlying cohort trajectory is still deeply below replacement.
        </p>

        <DivergenceTimeline
          title="Korea TFR 1990-2024 with six branch-point markers"
          series={KOREA_TFR_SERIES}
          splitPoint={BRANCH_MARKERS}
          yLabel="TFR (births per woman)"
          xLabel="year"
          yMin={0.5}
          yMax={2.0}
        />

        <p className="finding-prose">
          Three structural facts about how this trajectory unfolded:
        </p>
        <ol className="finding-numbered-list">
          <li>The decline was not linear. It had moments of partial recovery (1995, 2010-2015) and moments of acceleration (1998-2003, 2018-2023). Each moment was driven by specific conditions, not a single underlying cause.</li>
          <li>Policy responses were chronically delayed. The first explicit pronatalist plan arrived in 2005, when TFR had already been below 1.5 for over a decade.</li>
          <li><strong>The decline accelerated <em>after</em> the 2005 plan launched.</strong> Policy investment scaled from ~0.5% GDP in 2005 to ~1.65% by 2022, but TFR fell from 1.08 to 0.72 over the same period. The lever-vs-locking distinction explains why.</li>
        </ol>
      </FindingSection>

      {/* ─── § The branch points ─── */}
      <FindingSection eyebrow="§ The branch points" title="Six moments where the trajectory could plausibly have gone differently.">

        <BranchCard
          num={1}
          title="1997-1998 IMF crisis"
          classification="LEVER · missed"
          tone="oxblood"
          magnitude="largest documented lever in the corpus"
        >
          <p className="finding-prose">
            <strong>The moment:</strong> Korea&rsquo;s currency collapse + IMF restructuring agreement. Mass unemployment among prime-age workers. Chaebol restructuring shifted permanent jobs toward irregular work; the regular employment share fell from ~80% to ~65% over five years.
          </p>
          <p className="finding-prose">
            <strong>What actually happened:</strong> Restructuring proceeded with no protection for family-formation supports. Marriage rates fell sharply; couples postponed children; many never had them. The cohort born 1965-1972 was the most affected. TFR fell from 1.63 (1995) to 1.16 (2003).
          </p>
          <p className="finding-prose">
            <strong>The counterfactual:</strong> If labor-market restructuring had carved out family-formation protections (parental-leave guarantees independent of employment status, housing support for newlyweds, employment-stability tracks for parents), the 1997-2003 TFR collapse might have been ~0.20 less severe — putting Korea on a trajectory toward Italy&rsquo;s ~1.3, not toward 1.0.
          </p>
          <p className="finding-prose">
            <em>Why this was a lever:</em> The IMF restructuring was a once-in-a-generation policy moment with structural permanence. Korea chose technocratic efficiency; family-formation was treated as private rather than infrastructure. <strong>No subsequent intervention has produced movement of comparable magnitude.</strong>
          </p>
        </BranchCard>

        <BranchCard
          num={2}
          title="2002-2005 Seoul housing bubble"
          classification="LEVER · missed"
          tone="oxblood"
          magnitude="medium"
        >
          <p className="finding-prose">
            <strong>The moment:</strong> Seoul housing prices doubled in 3 years (2002-2005), driven by chaebol-employee asset accumulation and policy-encouraged home ownership. Young couples — exactly the cohort most affected by Branch 1&rsquo;s employment shock — found themselves priced out of metro housing.
          </p>
          <p className="finding-prose">
            <strong>What actually happened:</strong> No structural intervention. Some price-stabilization measures attempted in 2003-2005 but easily evaded. Marriage age delayed further; the proportion of women never marrying by age 35 began rising sharply.
          </p>
          <p className="finding-prose">
            <strong>The counterfactual:</strong> Singapore-model intervention (HDB-tier housing reserved for generational stability + price gating) might have preserved metro housing as accessible to young families. Estimated 0.05-0.10 TFR difference at 2015.
          </p>
          <p className="finding-prose">
            <em>Why this was a lever:</em> Housing dynamics are policy-tractable on a decade timescale if intervention happens early. Korea chose property-as-wealth-vehicle over property-as-family-infrastructure. Once that choice cemented, reversal became structurally impossible.
          </p>
        </BranchCard>

        <BranchCard
          num={3}
          title="2005 first Master Plan"
          classification="LEVER · partially missed (tempo only)"
          tone="cognac"
          magnitude="medium tempo · low cohort"
        >
          <p className="finding-prose">
            <strong>The moment:</strong> Korea launched its first explicit pronatalist plan when TFR was already 1.08. Spending: ~0.5% GDP initially, scaling toward 1.65% by 2022. Hungary-tier intensity (4-6% GDP) was never seriously considered.
          </p>
          <p className="finding-prose">
            <strong>What actually happened:</strong> Technocratic frame; political coalition-building around demographic security as national identity (Orbán&rsquo;s strategy from 2010) didn&rsquo;t emerge in Korea.
          </p>
          <p className="finding-prose">
            <strong>The counterfactual:</strong> If Korea had matched Hungary&rsquo;s 4%+ GDP commitment with similar duration, the tempo effect would have been visible. Estimated 0.10-0.20 <em>temporary</em> TFR boost (similar to Hungary&rsquo;s 1.25→1.59), with similar reversion afterward. <strong>Tempo, not cohort.</strong>
          </p>
          <p className="finding-prose">
            <em>Why this was a partial lever:</em> By 2005, the underlying cohort trajectory was already operative (Branch 1 + Branch 2 had locked in by 2003). The deepest intervention possible at this branch was tempo-visible but not cohort-durable.
          </p>
        </BranchCard>

        <BranchCard
          num={4}
          title="2015-2018 employment polarization + gender backlash"
          classification="LOCKING (small lever effect)"
          tone="plum"
          magnitude="negligible"
        >
          <p className="finding-prose">
            <strong>The moment:</strong> Korean labor markets continued post-1997 polarization (regular vs irregular work, large-firm vs small-firm). The #4B movement (no marriage, no children, no dating, no sex with men) emerged among educated Korean women, driven by structural inequities in domestic labor + career trade-offs + housing dynamics + status politics.
          </p>
          <p className="finding-prose">
            <strong>What actually happened:</strong> No policy recognition of the gendered nature of fertility decision-making. Continued framing of fertility as a workplace + childcare optimization problem rather than a relational + status-hierarchy problem.
          </p>
          <p className="finding-prose">
            <strong>The counterfactual:</strong> If Korean policy had recognized the gendered structural backlash as a primary variable, interventions in 2015-2018 might have addressed asymmetric domestic-labor dynamics. Estimated TFR effect: 0.02-0.05 at 2025. <strong>Small.</strong>
          </p>
          <p className="finding-prose">
            <em>Why this was a locking:</em> By 2015, the cohort dynamics were already set. The #4B movement was a <em>symptom</em> of accumulated lock-in, not a cause that could be neutralized by policy. The structural conditions producing it were upstream.
          </p>
        </BranchCard>

        <BranchCard
          num={5}
          title="2020-2022 COVID + housing spike"
          classification="LOCKING"
          tone="plum"
          magnitude="negligible"
        >
          <p className="finding-prose">
            <strong>The moment:</strong> COVID disrupted marriage formation; Seoul housing prices surged again; irregular work expanded.
          </p>
          <p className="finding-prose">
            <strong>What actually happened:</strong> TFR fell from 0.84 (2020) to 0.72 (2023). Marriage rates collapsed.
          </p>
          <p className="finding-prose">
            <strong>The counterfactual:</strong> Even aggressive housing intervention or marriage support during COVID would have produced minimal cohort effect. Estimated TFR effect: 0.01-0.03 at 2025.
          </p>
          <p className="finding-prose">
            <em>Why this was a locking:</em> Pure compounding of accumulated lock-in. By 2020 the structural conditions producing low fertility were entrenched across multiple variables (housing, employment, gender, status, peer-network). Marginal intervention couldn&rsquo;t move the trajectory.
          </p>
        </BranchCard>

        <BranchCard
          num={6}
          title="2024-present recognition window"
          classification="LEVER (currently open) · institutional adaptation"
          tone="sage"
          magnitude="trivial for TFR · large for institutional adaptation"
        >
          <p className="finding-prose">
            <strong>The moment:</strong> Korea&rsquo;s 0.72 → 0.75 uptick + the emerging recognition that policy spending alone won&rsquo;t reverse the trajectory.
          </p>
          <p className="finding-prose">
            <strong>What might happen:</strong> Either Korea (a) sustains tempo recovery into 0.85+ via continued tactical intervention, with no cohort change underneath, or (b) treats the moment as recognition-and-redirect — pivoting away from fertility-recovery framing toward demographic-adaptation framing (smaller-population, older-population institutional design). Both are reasonable; neither produces a return to 1.5+.
          </p>
          <p className="finding-prose">
            <em>Why this is a (small) lever:</em> Not for fertility. For institutional adaptation. The branch point currently open isn&rsquo;t about reversing decline — it&rsquo;s about whether Korea acknowledges the lock-in early enough to redirect institutional design before the demographic transition produces structural collapse (pension solvency, labor force, infrastructure maintenance).
          </p>
        </BranchCard>
      </FindingSection>

      {/* ─── § The counterfactual paths ─── */}
      <FindingSection eyebrow="§ The counterfactual paths" title="What might have happened differently at each branch.">
        <p className="finding-prose">
          If Korea had handled Branch 1 differently — if 1997-1998 restructuring had carved out family-formation protections — the cumulative trajectory would have looked like Italy&rsquo;s or Spain&rsquo;s, not the lowest-low cluster. Italy&rsquo;s TFR is 1.30 in 2024; Spain&rsquo;s is 1.16. Both have severe demographic challenges, but neither sits at 0.72-0.75.
        </p>
        <p className="finding-prose">
          If Korea had ALSO handled Branch 2 (housing) differently, the trajectory might have landed near France&rsquo;s 2010-2024 range (1.65 with rescue-effect blending). Still sub-replacement, but in the manageable range, not the structural-emergency range.
        </p>
        <p className="finding-prose">
          If Korea had ALSO matched Hungary&rsquo;s Branch 3 spending intensity, the <em>headline TFR</em> might have shown a 0.10-0.20 tempo bump for a decade — peaking around 1.30 — before reverting toward the underlying cohort rate (which is what Hungary 2024 just demonstrated). Cosmetic but not structural.
        </p>
        <p className="finding-prose">
          <strong>The cumulative counterfactual:</strong> Korea handling all three early levers (1997 + 2002-2005 + 2005-plan-with-Hungary-tier-spending) might have produced a 2024 TFR of 1.20-1.35 instead of 0.72-0.75. <em>Still below replacement. Still demographic decline.</em> But not the structural emergency Korea has now.
        </p>
        <p className="finding-prose">
          The missed-leverage view: the difference between the lever-handled counterfactual (~1.30 TFR) and the actual outcome (0.75 TFR) is <strong>0.55 TFR.</strong> That&rsquo;s ~half of Korea&rsquo;s cohort fertility. Path-dependence is real and the magnitude is substantial.
        </p>
      </FindingSection>

      {/* ─── § The levers ─── */}
      <FindingSection eyebrow="§ The levers" title="Three branch points were levers. Three were lockings.">
        <p className="finding-prose">
          The lever-magnitude ranking, from largest counterfactual divergence to smallest:
        </p>

        <MagnitudeBars
          title="Lever magnitude — counterfactual TFR divergence by branch point [mechanism-dossier estimates, not measured; see §How we could be wrong]"
          items={LEVER_RANKING}
          valueLabel="counterfactual TFR difference (estimate)"
        />

        <p className="finding-prose">
          The pattern: <strong>the levers were all early, and they were all about structural infrastructure (employment, housing, family-formation protections), not about spending.</strong> The lockings (Branches 4-5) are downstream of the levers. By the time spending intervention scaled (2010-2022), the levers had closed.
        </p>
        <p className="finding-prose">
          This is the path-dependent finding: <strong>leverage in fertility decline closes earlier than the policy literature usually assumes.</strong> The 2005 master plan was already late.
        </p>
      </FindingSection>

      {/* ─── § What this tells us ─── */}
      <FindingSection eyebrow="§ What this tells us" title="The path-dependent nature of fertility decline.">

        <CausalChainDiagram
          title="How structural levers compound into lock-in"
          steps={LOCKIN_CHAIN}
        />

        <FindingSubhead>For Korea today</FindingSubhead>
        <p className="finding-prose">
          The structural fertility lever is closed. The remaining intervention — Branch 6 — is about institutional adaptation, not fertility recovery. Continued pronatalist spending will produce tempo effects (the 2024 uptick may be one such effect) but will not produce cohort recovery to 1.0+, much less 1.5+. <strong>Honest policy framing pivots from &ldquo;fertility crisis&rdquo; to &ldquo;smaller-population institutional redesign.&rdquo;</strong>
        </p>

        <FindingSubhead>For other declining-fertility countries</FindingSubhead>
        <p className="finding-prose">
          Korea&rsquo;s lock-in pattern is generalizable. Countries currently at TFR &lt; 1.5 are mostly at the equivalent of Branches 4-6 — past the lever window. Aggressive spending will produce Hungarian-style tempo effects (real, visible, transient) but will not produce sustained cohort recovery.
        </p>
        <p className="finding-prose">
          The actionable lesson is for countries currently above 1.5 but on declining trajectory: <strong>watch the leverage windows, intervene early on structural variables (employment stability, housing affordability, family-formation infrastructure), don&rsquo;t wait until TFR falls below 1.3 to recognize the crisis.</strong> By the time TFR is 1.3, the levers are already mostly closed.
        </p>
        <p className="finding-prose">
          The cross-country anchor: Hungary 2010 was at TFR 1.25 — already past the lever window. Hungary&rsquo;s investment produced tempo effects (visible) but not cohort recovery (invisible underneath, now reverting). The Korea-Hungary pair finding documents this directly.
        </p>
      </FindingSection>

      {/* ─── § How we could be wrong ─── */}
      <FindingSection eyebrow="§ How we could be wrong" title="Counterfactual analysis is speculative by nature.">
        <p className="finding-prose">
          Every claim in this finding is &ldquo;if X had been different, Y would have followed&rdquo; — and we cannot rerun history. The discipline is in the cross-country anchoring (Italy, Spain, France, Hungary, Israel, Estonia) and the falsifiable forward predictions.
        </p>

        <FindingSubhead>Selection of branch points is partly choice</FindingSubhead>
        <p className="finding-prose">
          We named six branch points; an alternative analysis could name 4 or 9. The risk: the framework may be confirmation-biased toward branches that fit a &ldquo;structural lever&rdquo; narrative.
        </p>

        <FindingSubhead>The lever vs locking distinction may be too neat</FindingSubhead>
        <p className="finding-prose">
          Real path-dependence is continuous, not binary. Branches 4-5 may have small lever effects we&rsquo;re calling zero. Branch 3 may have larger cohort effects we&rsquo;re calling zero. The classification is honest but rough.
        </p>

        <FindingSubhead>Korea may not be representative</FindingSubhead>
        <p className="finding-prose">
          Korea&rsquo;s TFR trajectory is unprecedented for peacetime modern economies. The counterfactual reasoning may not generalize to countries with different starting conditions, cultural-narrative density, institutional trajectories.
        </p>

        <FindingSubhead>The lever-window-closes-early claim is the load-bearing inference</FindingSubhead>
        <p className="finding-prose">
          If subsequent data shows that Hungary&rsquo;s 2010-2021 tempo recovery was actually partial cohort recovery (rejected by current N-IUSSP and AEI analysis but possible under stricter cohort completion data in 2028+), the lever-window-closes-early claim weakens. The Korea-Hungary pair finding&rsquo;s falsifier directly tests this.
        </p>

        <TornadoPlot
          title="Falsifier sensitivity — what shifts the lock-in framing"
          baselineLabel="finding holds"
          dimensions={LOCKIN_FALSIFIER}
        />

        <FindingSubhead>What would change my mind</FindingSubhead>
        <ol className="finding-numbered-list">
          <li>A country at TFR &lt; 1.0 produces sustained cohort recovery (TFR back to 1.5+) within 10 years via policy intervention alone. Falsifies the lock-in claim entirely.</li>
          <li>Korean cohort completion data (women born 1985-1995, available ~2030) shows meaningfully different completed fertility than current cohort projections suggest.</li>
          <li>Counterfactual replication failure: another country that handled its equivalent of Branches 1-3 differently shows a <em>similar</em> trajectory to Korea&rsquo;s. That would suggest the levers we identified weren&rsquo;t real.</li>
        </ol>
      </FindingSection>

      {/* ─── § What this implies ─── */}
      <FindingSection eyebrow="§ What this implies" title="Reflexivity HIGH — diagnostic + menu mode, not prescriptive.">
        <p className="finding-prose">
          <em>Note on reflexivity: this finding is rated HIGH reflexivity. Predictions about fertility trajectories can affect the systems being predicted. The implications below are framed as diagnostic + menu of tested-intervention positions, not as prescriptive policy advice.</em>
        </p>

        <FindingSubhead>For demographic policymakers in countries above TFR 1.5</FindingSubhead>
        <ul className="finding-bullets">
          <li>The leverage windows are <em>now</em>, not when TFR has already fallen below 1.3. Watch the structural variables: employment stability, housing affordability, family-formation infrastructure.</li>
          <li>Once-in-a-generation policy moments (financial crisis restructuring, housing-policy resets, new social-contract debates) are the levers. Most policy-spending levers come too late.</li>
          <li>Spending intensity — even Hungarian 4-6% GDP — produces tempo effects, not cohort recovery. Do not mistake one for the other.</li>
        </ul>

        <FindingSubhead>For countries below TFR 1.3 (Korea, Italy, Spain, Japan, China, the EU low-low cluster)</FindingSubhead>
        <p className="finding-prose">
          The honest read: structural lock-in has likely set in. The remaining policy menu is <em>institutional adaptation</em>, not fertility recovery. Pension solvency, labor-force planning, infrastructure-maintenance models, social-contract redesign for smaller-older-populations.
        </p>

        <PredictionsBlock
          title="Pre-registered forward predictions (2026-05-07)"
          predictions={LOCKIN_PREDICTIONS}
        />
      </FindingSection>

      {/* ─── § What we're watching next ─── */}
      <FindingSection eyebrow="§ What we're watching next" title="Three open questions worth chasing.">
        <p className="finding-prose">
          <strong>1. Cross-country counterfactual replication.</strong> Italy, Spain, Japan all have similar trajectory shapes to Korea&rsquo;s. Did they have analogous branch points? Were any handled differently? A follow-up Form 5 finding on (say) Italy 1992-2024 would test whether the lock-in pattern is structural or Korea-specific.
        </p>
        <p className="finding-prose">
          <strong>2. The recognition window for countries currently at 1.5-1.7.</strong> US (1.62), UK (1.49), France (1.65), Sweden (1.45). These are at branch points right now. Which are they? When do they close? A forward-looking finding pre-registering branch-point identification for these countries would put the framework to the test.
        </p>
        <p className="finding-prose">
          <strong>3. Korea&rsquo;s institutional adaptation pivot.</strong> If Branch 6 closes as a missed lever (Korea continues fertility-recovery framing instead of adaptation framing), what are the structural consequences over 2030-2050? A follow-up Form 5 on the institutional-adaptation lock-in would map the second-order branches.
        </p>
      </FindingSection>

      {/* ─── Footer ledger ─── */}
      <footer className="finding-footer">
        <div className="finding-footer-eyebrow">Footer ledger</div>

        <dl className="finding-meta">
          <dt>Methodology Version</dt>
          <dd>Lab V2.0</dd>

          <dt>Form</dt>
          <dd>Form 5 — Counterfactual walk</dd>

          <dt>Reflexivity</dt>
          <dd>HIGH · <code>mode: diagnostic + menu</code></dd>

          <dt>Sample size</dt>
          <dd>N=1 trajectory (Korea); 6 branch points within. Anchor cases drawn from population-decline corpus + Korea-Hungary pair finding.</dd>

          <dt>Epistemic status</dt>
          <dd>Mechanism documented · counterfactual reasoning explicit. Upgrade path to causal-chain traceable contingent on cross-country counterfactual replication.</dd>

          <dt>Pre-registered</dt>
          <dd>2026-05-07. Resolutions 2028 (Korea TFR) + 2030+ (cohort completion).</dd>

          <dt>Sources</dt>
          <dd>
            UN World Population Prospects 2024 · Statistics Korea (KOSIS) TFR series 1990-2024 · IMF + Korean labor-policy archives for 1997-1998 restructuring · Continuum Phase A Batch 1 (<code>docs/CONTINUUM_PHASE_A_BATCH_1.md</code>) · Korea-Hungary pair finding (<FindingCite handle="lab:finding/popdec/2026/korea-hungary-divergence/v1" />)
          </dd>

          <dt>Falsifier</dt>
          <dd>
            Cohort completion data showing Korean 1990-1995 cohort meaningfully above current trajectory; OR another country&rsquo;s sub-1.0 TFR + sustained recovery demonstrating the lock-in is reversible; OR counterfactual replication failure in another similarly-trajected country.
          </dd>

          <dt>Citation handle</dt>
          <dd><code>lab:finding/popdec/2026/korea-lockin/v1</code></dd>
        </dl>

        <div className="finding-citation">
          <strong>Cite as:</strong> Mai, T. (2026). <em>Korea&rsquo;s Lock-In: A Counterfactual Walk Through Six Branch Points in the Fertility Decline.</em> Lab Finding <code>lab:finding/popdec/2026/korea-lockin/v1</code>. Methodology v2.0. Pre-registered 2026-05-07.
        </div>

        <div className="finding-engage">
          <div className="finding-footer-eyebrow">How to engage</div>
          <ul className="finding-bullets">
            <li><strong>Challenge:</strong> propose a branch-point reclassification (lever→locking or vice versa); cite specific evidence.</li>
            <li><strong>Cite:</strong> the citation handle is stable across versions; v1 is this draft.</li>
            <li><strong>Fork:</strong> take the methodology and run a parallel counterfactual walk on your country&rsquo;s trajectory (Italy 1992-2024, Japan 1990-2024, Argentina 2000-2024, etc.).</li>
          </ul>
        </div>

        <div className="finding-version">
          <div className="finding-footer-eyebrow">Update history</div>
          <p>v1 — 2026-05-07 — initial authoring (Tim Mai + Claude). Form 5 template applied. Pre-registered. Currently in Draft state.</p>
        </div>
      </footer>
    </article>
  );
}

// ─── Form 5 specific component ───────────────────────────────────────

function BranchCard({ num, title, classification, tone, magnitude, children }) {
  return (
    <div className={`finding-branch-card finding-branch-${tone}`}>
      <div className="finding-branch-head">
        <span className="finding-branch-num">Branch {num}</span>
        <span className={`finding-branch-class finding-branch-class-${tone}`}>{classification}</span>
        <span className="finding-branch-magnitude">{magnitude}</span>
      </div>
      <h3 className="finding-branch-title">{title}</h3>
      {children}
    </div>
  );
}
